import "./App.css";

import { Link } from "react-router-dom";
import { useState } from "react";
import { WEB_APP_URL } from "./config";

function PreRegister() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    profession: "",
    otherProfession: "",
    phoneNumber: "",
    emailAddress: "",
    preferredContact: {
      phone: false,
      email: false,
      text: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name.startsWith("contact_")) {
      const contactMethod = name.replace("contact_", "");
      setFormData((prev) => ({
        ...prev,
        preferredContact: {
          ...prev.preferredContact,
          [contactMethod]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate that if "Other" is selected, the otherProfession field is filled
    if (formData.profession === "Other" && !formData.otherProfession.trim()) {
      setSubmitStatus("validation_error");
      setIsSubmitting(false);
      return;
    }

    // Validate that at least one contact method is selected
    const hasContactMethod = Object.values(formData.preferredContact).some(
      Boolean
    );
    if (!hasContactMethod) {
      setSubmitStatus("contact_validation_error");
      setIsSubmitting(false);
      return;
    }

    try {
      // Google Apps Script Web App URL - you'll need to replace this with your actual URL
      const GOOGLE_SCRIPT_URL =
        "https://script.google.com/macros/s/AKfycbxove40qMFcQpT_pd_iJEiKM95JvmIktZ4ScPnN38GhKnf7GyoBz_7NJNdCBwJrPGHF/exec";

      // Prepare data for submission
      const submissionData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        profession:
          formData.profession === "Other"
            ? formData.otherProfession
            : formData.profession,
        phoneNumber: formData.phoneNumber,
        emailAddress: formData.emailAddress,
        preferredContact: formData.preferredContact,
      };

      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      // Since we're using no-cors mode, we can't read the response
      // But we assume success if no error is thrown
      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        profession: "",
        otherProfession: "",
        phoneNumber: "",
        emailAddress: "",
        preferredContact: {
          phone: false,
          email: false,
          text: false,
        },
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const professionOptions = [
    "Personal Trainer",
    "Run Coach",
    "Physiotherapist",
    "Yoga Instructor",
    "Pilates Instructor",
    "Nutritionist",
    "Sports Massage Therapist",
    "Strength and Conditioning Coach",
    "Swimming Instructor",
    "Dance Instructor",
    "Martial Arts Instructor",
    "Other",
  ];

  return (
    <div className="bg-black text-gray-200 min-h-screen">
      {/* Navbar */}
      <header className="bg-black bg-opacity-70 backdrop-blur-lg sticky top-0 w-full z-50 shadow-lg">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Fit-Match Logo"
              className="h-16 w-auto"
            />
            <Link to="/" className="text-2xl font-bold gradient-text">
              FIT-MATCH
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#how-it-works" className="nav-link font-medium">
              How It Works
            </Link>
            <Link to="/our-story" className="nav-link font-medium">
              Our Story
            </Link>
            <Link to="/professionals" className="nav-link font-medium">
              Pros
            </Link>
            <Link to="/training-providers" className="nav-link font-medium">
              Providers
            </Link>
            <Link to="/communities" className="nav-link font-medium">
              Communities
            </Link>
            <Link to="/facilities" className="nav-link font-medium">
              Facilities
            </Link>
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Login
            </a>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-4 6h4"
                ></path>
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:hidden bg-black bg-opacity-90 py-4`}
        >
          <Link
            to="/#how-it-works"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            How It Works
          </Link>
          <Link
            to="/our-story"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Our Story
          </Link>
          <Link
            to="/professionals"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Pros
          </Link>
          <Link
            to="/training-providers"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Providers
          </Link>
          <Link
            to="/communities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Communities
          </Link>
          <Link
            to="/facilities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Facilities
          </Link>
          <a
            href={WEB_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Login
          </a>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 md:py-32 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-purple-900 opacity-50"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight mb-6">
              Pre-register as a{" "}
              <span className="gradient-text">Professional</span>
            </h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8">
              Join our exclusive pre-registration list and be among the first to
              access Fit-Match when we launch.
            </p>
          </div>
        </section>

        {/* Registration Form Section */}
        <section className="py-20 bg-gray-950">
          <div className="container mx-auto px-6 max-w-2xl">
            <div className="bg-gray-800 p-8 rounded-xl shadow-xl">
              <h2 className="text-3xl font-bold text-center mb-8">
                Professional Information
              </h2>

              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-800 border border-green-600 rounded-lg">
                  <p className="text-green-200 text-center">
                    ✅ Thank you! Your pre-registration has been submitted
                    successfully. We will be in touch shortly before launching
                    to finish verification.
                  </p>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-800 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-center">
                    ❌ There was an error submitting your form. Please try
                    again.
                  </p>
                </div>
              )}

              {submitStatus === "validation_error" && (
                <div className="mb-6 p-4 bg-red-800 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-center">
                    ❌ Please specify your profession when "Other" is selected.
                  </p>
                </div>
              )}

              {submitStatus === "contact_validation_error" && (
                <div className="mb-6 p-4 bg-red-800 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-center">
                    ❌ Please select at least one preferred contact method.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium mb-2"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium mb-2"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="dateOfBirth"
                    className="block text-sm font-medium mb-2"
                  >
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium mb-2"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label
                    htmlFor="emailAddress"
                    className="block text-sm font-medium mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="emailAddress"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label
                    htmlFor="profession"
                    className="block text-sm font-medium mb-2"
                  >
                    Profession *
                  </label>
                  <select
                    id="profession"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                  >
                    <option value="">Select your profession</option>
                    {professionOptions.map((profession) => (
                      <option key={profession} value={profession}>
                        {profession}
                      </option>
                    ))}
                  </select>

                  {formData.profession === "Other" && (
                    <div className="mt-4">
                      <label
                        htmlFor="otherProfession"
                        className="block text-sm font-medium mb-2"
                      >
                        Please specify your profession *
                      </label>
                      <input
                        type="text"
                        id="otherProfession"
                        name="otherProfession"
                        value={formData.otherProfession}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-fit-match focus:border-transparent text-white"
                        placeholder="Enter your profession"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-4">
                    Preferred Contact Method *
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="contact_phone"
                        checked={formData.preferredContact.phone}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-fit-match bg-gray-700 border-gray-600 rounded focus:ring-blue-fit-match focus:ring-2"
                      />
                      <span className="ml-3 text-sm text-gray-300">
                        Phone Call
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="contact_email"
                        checked={formData.preferredContact.email}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-fit-match bg-gray-700 border-gray-600 rounded focus:ring-blue-fit-match focus:ring-2"
                      />
                      <span className="ml-3 text-sm text-gray-300">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="contact_text"
                        checked={formData.preferredContact.text}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-fit-match bg-gray-700 border-gray-600 rounded focus:ring-blue-fit-match focus:ring-2"
                      />
                      <span className="ml-3 text-sm text-gray-300">
                        Text Message
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Select at least one preferred contact method
                  </p>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-fit-match text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:bg-blue-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Pre-register Now"}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  By pre-registering, you agree to be contacted when Fit-Match
                  launches.
                  <br />
                  <Link to="/" className="text-blue-fit-match hover:underline">
                    ← Back to Home
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2024 Fit-Match. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PreRegister;
